/**
 * Email + password sign-in (accounts with the Platform ``password_auth``
 * flag). Reached from /email-entry or the method chooser at
 * /signin/email?email=; Platform only offers the method to existing users.
 *
 * Mobile parity: src/app/(auth)/signin/password/index.tsx.
 */

import React, { useEffect, useState } from "react";
import { Trans } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "lib/i18n/use-translation";

import {
  AuthLayout,
  Heading,
  PrimaryButton,
  TextField,
} from "apps/user/common/components/auth-layout";
import { useAuth } from "providers/auth";
import { loginWithPassword, OtpError } from "providers/auth-tokens";
import { logError } from "utility/Function";
import { setLastUsedEmail } from "utility/last-used-email";
import { trackEvent } from "utility/mixpanel";

import styles from "./otp-code-screen.module.scss";

// Inline like otp-code-screen: auth error copy is owned by the
// error-code contract work (WONE-1598).
const COPY = {
  invalid: "That email or password didn't match. Please try again.",
  lockout: "Too many attempts. Please try again later.",
  network: "Couldn't sign you in. Please try again.",
} as const;

const SCREEN = "signin_password";

const PasswordScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const { loadCurrentSession } = useAuth();
  const email = (
    new URLSearchParams(location.search).get("email") || ""
  ).toLowerCase();

  const [password, setPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [inlineError, setInlineError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!email) navigate("/email-entry", { replace: true });
  }, [email, navigate]);

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (submitting || !email) return;
    setInlineError(null);
    if (!password) {
      setFieldError(t("validation.passwordRequired"));
      trackEvent("Auth Validation Error Shown", {
        Field: "password",
        Screen: SCREEN,
        "Error Code": "required",
      });
      return;
    }
    setFieldError(null);
    setSubmitting(true);
    trackEvent("Password Sign In Submitted", {});
    try {
      await loginWithPassword({ email, password });
      setLastUsedEmail(email);
      await loadCurrentSession();
      const returnTo = (location.state as { returnTo?: string } | null)
        ?.returnTo;
      navigate(returnTo || "/");
    } catch (err) {
      const status = err instanceof OtpError ? err.status : null;
      let code = "network";
      let message: string = COPY.network;
      if (status === 401) {
        code = "bad_credentials";
        message = COPY.invalid;
      } else if (status === 429) {
        code = "rate_limited";
        message = COPY.lockout;
      } else {
        logError("Password login failed", err as Error);
      }
      setInlineError(message);
      trackEvent("Auth Server Error Shown", {
        Screen: SCREEN,
        "Error Code": code,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!email) return null;

  return (
    <AuthLayout variant="form" onBack={() => navigate(-1)}>
      <form className={styles["body"]} onSubmit={handleSubmit} noValidate>
        <Heading>{t("password.title")}</Heading>
        <p className={styles["email-line"]}>
          <Trans
            i18nKey="auth:password.body"
            values={{ email }}
            components={{ strong: <strong /> }}
          />
        </p>
        {/* Pairs the credential for password managers. */}
        <input
          type="email"
          autoComplete="username"
          value={email}
          readOnly
          hidden
        />
        <TextField
          label={t("password.fieldLabel")}
          type="password"
          name="password"
          placeholder={t("password.placeholder")}
          autoComplete="current-password"
          autoFocus
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setFieldError(null);
            setInlineError(null);
          }}
          error={fieldError}
          hasError={Boolean(inlineError)}
          disabled={submitting}
          trackingScreen={SCREEN}
          trackingField="password"
        />
        {inlineError && (
          <p className={styles["inline-error"]} role="alert">
            {inlineError}
          </p>
        )}
        <PrimaryButton type="submit" loading={submitting}>
          {t("password.title")}
        </PrimaryButton>
      </form>
    </AuthLayout>
  );
};

export default PasswordScreen;
